import type { RouteObject } from "react-router-dom";
import { redirect } from "react-router-dom";

import { addSlashes, withCompanyPrefix } from "../route.utils";
import { Spinner } from "@/components/ui/v2/spinner";
import {
  CompanyDataTransfer,
  CompanyDataTransferExportPage,
  CompanyDataTransferHistoryPage,
  CompanyDataTransferImportPage,
  CompanyDataTransferRunPage,
} from "@/utils/lazyLoadComponents";

export const COMPANY_DATA_TRANSFER_RUN_ID_KEY = "runId";

export enum COMPANY_DATA_TRANSFER_PATH_SEGMENTS {
  ROOT = "data-transfer",
  IMPORT = "import",
  EXPORT = "export",
  HISTORY = "history",
  RUNS = "runs",
  RUN = `:${COMPANY_DATA_TRANSFER_RUN_ID_KEY}`,
}

export const companyDataTransferRoutes: RouteObject = {
  path: COMPANY_DATA_TRANSFER_PATH_SEGMENTS.ROOT,
  handle: { breadcrumb: "Data Transfer" },
  HydrateFallback: Spinner,
  children: [
    // ── Tabbed shell ─────────────────────────────────────────────────────────
    {
      Component: CompanyDataTransfer,
      handle: { flushContent: true },
      children: [
        {
          index: true,
          loader: () => redirect(COMPANY_DATA_TRANSFER_PATHS.IMPORT),
        },
        {
          path: COMPANY_DATA_TRANSFER_PATH_SEGMENTS.IMPORT,
          handle: { breadcrumb: "Import" },
          Component: CompanyDataTransferImportPage,
        },
        {
          path: COMPANY_DATA_TRANSFER_PATH_SEGMENTS.EXPORT,
          handle: { breadcrumb: "Export" },
          Component: CompanyDataTransferExportPage,
        },
        {
          path: COMPANY_DATA_TRANSFER_PATH_SEGMENTS.HISTORY,
          handle: { breadcrumb: "History" },
          Component: CompanyDataTransferHistoryPage,
        },
      ],
    },
    /*
     * The wizard sits OUTSIDE the tab shell: a multi-minute run with its own
     * stepper does not belong inside a tab strip. `runs/:runId` rather than a bare
     * `:runId` so it can never be confused with a tab segment.
     */
    {
      path: `${COMPANY_DATA_TRANSFER_PATH_SEGMENTS.RUNS}/${COMPANY_DATA_TRANSFER_PATH_SEGMENTS.RUN}`,
      // Serves imports and exports alike, so the crumb stays neutral — the page
      // header names which one it is.
      handle: { breadcrumb: "Details" },
      Component: CompanyDataTransferRunPage,
    },
  ],
};

export const COMPANY_DATA_TRANSFER_PATHS = Object.freeze({
  ROOT: withCompanyPrefix(addSlashes(COMPANY_DATA_TRANSFER_PATH_SEGMENTS.ROOT)),
  IMPORT: withCompanyPrefix(
    addSlashes(
      COMPANY_DATA_TRANSFER_PATH_SEGMENTS.ROOT,
      COMPANY_DATA_TRANSFER_PATH_SEGMENTS.IMPORT,
    ),
  ),
  EXPORT: withCompanyPrefix(
    addSlashes(
      COMPANY_DATA_TRANSFER_PATH_SEGMENTS.ROOT,
      COMPANY_DATA_TRANSFER_PATH_SEGMENTS.EXPORT,
    ),
  ),
  HISTORY: withCompanyPrefix(
    addSlashes(
      COMPANY_DATA_TRANSFER_PATH_SEGMENTS.ROOT,
      COMPANY_DATA_TRANSFER_PATH_SEGMENTS.HISTORY,
    ),
  ),
  RUN: (runId: number | string) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_DATA_TRANSFER_PATH_SEGMENTS.ROOT,
        COMPANY_DATA_TRANSFER_PATH_SEGMENTS.RUNS,
        encodeURIComponent(String(runId)),
      ),
    ),
});
